import React, { useState } from 'react'
import {
  Calendar as CalendarIcon,
  FileText,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { useGetUserBotAlertDetailsQuery } from '@/redux/service/bot'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import dayjs from 'dayjs'
import { CustomPagination } from './CustomPagination'
import { cn } from '@/lib/utils'

interface BotAlertmodelProps {
  isOpen: boolean
  onClose: () => void
  userId: number | null
  userName: string | null
  profile: string | null
}

const BotAlertmodel: React.FC<BotAlertmodelProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  profile,
}) => {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [startDateOpen, setStartDateOpen] = useState(false)
  const [endDateOpen, setEndDateOpen] = useState(false)

  const [sortBy, setSortBy] = useState<string | undefined>()
  const [order, setOrder] = useState<'ASC' | 'DESC' | undefined>()

  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (order === 'ASC') {
        setOrder('DESC')
      } else if (order === 'DESC') {
        setSortBy(undefined)
        setOrder(undefined)
      }
    } else {
      setSortBy(column)
      setOrder('ASC')
    }
    setPage(1)
  }

  const getSortIcon = (column: string) => {
    if (sortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-2 opacity-50 inline-block' />
    if (order === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-2 inline-block' />
    return <ArrowDown className='w-3 h-3 ml-2 inline-block' />
  }

  const { data: detailData, isFetching: isDetailsLoading } =
    useGetUserBotAlertDetailsQuery(
      {
        userId: userId || 0,
        page,
        limit,
        startDate: startDate
          ? dayjs(startDate).format('YYYY-MM-DD')
          : undefined,
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
        sort_by: sortBy,
        order,
      },
      { skip: !userId || !isOpen }
    )

  const totalPages = detailData?.pagination?.totalPages || 1
  const totalRecords = detailData?.pagination?.total || 0

  const handleResetFilters = () => {
    setStartDate(undefined)
    setEndDate(undefined)
    setSortBy(undefined)
    setOrder(undefined)
    setPage(1)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-w-2xl w-full p-0 overflow-hidden bg-background border-border rounded-xl shadow-2xl max-h-[90vh] flex flex-col'>
        <div className='flex flex-col h-full overflow-hidden'>
          {/* HEADER */}
          <div className='px-8 py-6 border-b border-border flex items-center justify-between bg-muted/30 rounded-xl '>
            <div className='flex items-center gap-4'>
              <div className='h-12 w-12 bg-primary/10 flex items-center justify-center text-primary font-bold text-xl shadow-sm '>
                {profile ? (
                  <img
                    src={profile}
                    alt={userName ?? 'User Profile'}
                    className='w-full h-full object-cover'
                    onError={e => {
                      ;(e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                ) : (
                  <span className='text-xs font-bold text-primary'>
                    {userName ? userName.charAt(0).toUpperCase() : ''}
                  </span>
                )}
              </div>
              <div className='space-y-1'>
                <DialogTitle className='text-2xl font-bold text-foreground tracking-tight'>
                  Alert History
                </DialogTitle>
                <DialogDescription className='text-sm text-muted-foreground'>
                  Missing timesheet logs history for{' '}
                  <span className='font-bold text-foreground'>{userName}</span>
                </DialogDescription>
              </div>
            </div>
          </div>

          <div className='p-8 space-y-6 flex-1 overflow-hidden flex flex-col'>
            {/* FILTER SECTION */}
            <div className='flex flex-col sm:flex-row items-end gap-4 bg-muted/20 p-5 rounded-2xl border border-border/50 shadow-sm'>
              <div className='flex items-center gap-4 flex-1 w-full'>
                {/* Start Date */}
                <div className='space-y-2 flex-1'>
                  <label className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'>
                    Start Date
                  </label>
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        className={cn(
                          'w-full h-10 border-border rounded-lg bg-background font-medium text-xs justify-start px-3 transition-all hover:bg-muted/50',
                          !startDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className='mr-2 h-3.5 w-3.5 opacity-50' />
                        {startDate
                          ? dayjs(startDate).format('MMM DD, YYYY')
                          : 'Pick date'}
                        <ChevronDown className='ml-auto h-4 w-4 opacity-30' />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className='w-auto p-0 rounded-xl border-border shadow-xl'
                      align='start'
                    >
                      <Calendar
                        mode='single'
                        captionLayout='dropdown'
                        selected={startDate}
                        fromYear={2020}
                        toYear={2030}
                        onSelect={date => {
                          setStartDate(date)
                          setPage(1)
                          setStartDateOpen(false) // Close calendar

                          if (
                            endDate &&
                            date &&
                            dayjs(endDate).isBefore(dayjs(date))
                          ) {
                            setEndDate(undefined)
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* End Date */}
                <div className='space-y-2 flex-1'>
                  <label className='text-xs font-bold uppercase tracking-wider text-muted-foreground px-1'>
                    End Date
                  </label>
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        className={cn(
                          'w-full h-10 border-border rounded-lg bg-background font-medium text-xs justify-start px-3 transition-all hover:bg-muted/50',
                          !endDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className='mr-2 h-3.5 w-3.5 opacity-50' />
                        {endDate
                          ? dayjs(endDate).format('MMM DD, YYYY')
                          : 'Pick date'}
                        <ChevronDown className='ml-auto h-4 w-4 opacity-30' />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className='w-auto p-0 rounded-xl border-border shadow-xl'
                      align='end'
                    >
                      <Calendar
                        mode='single'
                        selected={endDate}
                        captionLayout='dropdown'
                        fromYear={2020}
                        toYear={2030}
                        onSelect={date => {
                          setEndDate(date)
                          setPage(1)
                          setEndDateOpen(false) // Close calendar
                        }}
                        disabled={startDate ? { before: startDate } : undefined}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <Button
                variant='outline'
                size='sm'
                onClick={handleResetFilters}
                className='h-10 px-6 rounded-lg border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-all'
              >
                Reset
              </Button>
            </div>

            {/* CONTENT TABLE */}
            <div className='rounded-sm border border-border overflow-hidden flex flex-col flex-1 shadow-sm'>
              <Table className='border-none'>
                <TableHeader className='bg-muted/50 sticky top-0 z-10 backdrop-blur-sm'>
                  <TableRow className='hover:bg-transparent border-b border-border'>
                    <TableHead
                      className='px-6 py-4 font-bold text-foreground text-[11px] uppercase tracking-wider cursor-pointer'
                      onClick={() => handleSort('alert_date')}
                    >
                      Alert Date {getSortIcon('alert_date')}
                    </TableHead>
                    <TableHead
                      className='px-6 py-4 font-bold text-foreground text-[11px] uppercase tracking-wider text-center cursor-pointer'
                      onClick={() => handleSort('missing_days_count')}
                    >
                      Missing Count {getSortIcon('missing_days_count')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className='divide-y divide-border'>
                  {isDetailsLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell className='px-6 py-4'>
                          <Skeleton className='h-4 w-32 rounded-md' />
                        </TableCell>
                        <TableCell className='px-6 py-4 text-center'>
                          <Skeleton className='h-4 w-12 mx-auto rounded-md' />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : detailData?.alerts?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className='h-60 text-center'>
                        <div className='flex flex-col items-center justify-center gap-4 text-muted-foreground'>
                          <div className='p-6 bg-muted/50 rounded-full'>
                            <FileText className='w-12 h-12 opacity-20' />
                          </div>
                          <div className='space-y-1'>
                            <p className='font-bold text-foreground text-lg'>
                              No history found
                            </p>
                            <p className='text-sm max-w-[250px] mx-auto'>
                              No alert records were found for the selected
                              criteria.
                            </p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    detailData?.alerts?.map((alert, idx) => (
                      <TableRow
                        key={idx}
                        className='hover:bg-muted/30 transition-colors'
                      >
                        <TableCell className='px-6 py-4'>
                          <div className='flex items-center gap-2.5 text-foreground font-medium'>
                            <CalendarIcon className='w-3.5 h-3.5 text-muted-foreground' />
                            {dayjs(alert.alert_date).format('MMMM DD, YYYY')}
                          </div>
                        </TableCell>
                        <TableCell className='px-6 py-4 text-center'>
                          <span className='inline-flex items-center px-3 py-1 rounded-md bg-muted font-mono font-bold text-xs border border-border shadow-sm'>
                            {String(alert.missing_days_count).padStart(2, '0')}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* PAGINATION SECTION */}
            {!isDetailsLoading && totalPages >= 1 && (
              <div className='pt-2'>
                <CustomPagination
                  page={page}
                  setPage={setPage}
                  limit={limit}
                  setLimit={setLimit}
                  totalPages={totalPages}
                  totalRecords={totalRecords}
                />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default BotAlertmodel
